import { Component, Input, EventEmitter, Output, OnChanges} from '@angular/core';
import { SelectItem } from 'primeng-lts/api';
import { CargoReportWeeklyScheduleV2, CargoReportDailyScheduleV2, CargoReportMonthlyScheduleV2 } from '@al/cargo';
import { AlNotificationDictionariesUtility } from '../services/al-notification-dictionaries-utility';

@Component({
    selector: 'al-cadence-selector',
    templateUrl: './al-cadence-selector.component.html',
    styleUrls: ['./al-cadence-selector.component.scss']
})

export class AlCadenceSelectorComponent implements OnChanges{

    @Input() frequencies: string[] | false = []; // each report can have differente valid frecuency false or ["daily", "weekly", "monthly"],
    @Input() initialSchedule: undefined | 'every_15_minutes' | 'asap' | {
        daily?: CargoReportDailyScheduleV2;
        weekly?: CargoReportWeeklyScheduleV2;
        monthly?: CargoReportMonthlyScheduleV2;
    };
    @Input() minDate?: Date;
    // Emitter which emits the selected item and the hour
    @Output() onCadenceChanged: EventEmitter<{ daily: CargoReportDailyScheduleV2; } | { weekly: CargoReportWeeklyScheduleV2; }
        | { monthly: CargoReportMonthlyScheduleV2; } | 'every_15_minutes' | 'asap' | string > = new EventEmitter();

    @Output() onMonthSelectedChanged: EventEmitter<number> = new EventEmitter();

    public dayOfMonth: {label:string;value:number}[] = [];
    public scheduledDayOfMonth: number = 1;
    public dayOfWeek: {label:string;value:string}[] = [
        { label: "Monday", value: "monday" },
        { label: "Tuesday", value: "tuesday" },
        { label: "Wednesday", value: "wednesday" },
        { label: "Thursday", value: "thursday" },
        { label: "Friday", value: "friday" },
        { label: "Saturday", value: "saturday" },
        { label: "Sunday", value: "sunday" },
    ];
    public scheduledDayOfWeek: "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday" = "monday";
    public maxDate: Date = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1, 0, 0, 0, 0));

    options: SelectItem[] = [];
    selectedOption: { key: string, subLabel: string, hourInputLabel?: string } | undefined = undefined;
    hourSelected: Date | null = null;
    hourFieldName: string = '';
    dictionaries = new AlNotificationDictionariesUtility();
    public isInvalidDate: boolean = false;
    showMonthlyTimeFrames:boolean = false;
    monthlyTimeFrames = [
        { label: 'Last Month', value: 1 },
        { label: '2 Months Ago', value: 2 },
        { label: '3 Months Ago', value: 3 },
        { label: '4 Months Ago', value: 4 },
        { label: '5 Months Ago', value: 5 },
        { label: '6 Months Ago', value: 6 },
        { label: '7 Months Ago', value: 7 },
        { label: '8 Months Ago', value: 8 },
        { label: '9 Months Ago', value: 9 },
        { label: '10 Months Ago', value: 10 },
        { label: '11 Months Ago', value: 11 },
        { label: '12 Months Ago', value: 12 },
    ];
    monthSelected:number = 1;

    constructor() {
        this.initialSetup();
    }

    ngOnChanges() {
        this.initialSetup();
    }

    /**
     * setup the frecuency and the default values
     */
    initialSetup = () => {
        this.options = [];

        if (this.frequencies instanceof Array) {
            this.setOptions(this.frequencies);
        }

        this.selectedOption = this.options.length > 0 ? this.options[0].value : undefined;

        this.hourSelected = null;

        // in edit mode if a schedule is passed
        if (this.initialSchedule) {
            const runTime = this.dictionaries.getRunTime(this.initialSchedule);
            this.hourSelected = runTime ? new Date(runTime): new Date();
            const cadenceSelected = this.dictionaries.getCadenceName(this.initialSchedule);
            const findOption = this.options.filter( option => cadenceSelected === option.value.key );
            if(findOption && findOption.length > 0){
                this.selectedOption = findOption[0].value;
            }
        }

        this.dayOfMonth = [];
        for (let n = 1; n <= 31; n++) {
            this.dayOfMonth.push({label: `${n}`, value: n});
        }

        this.onBuildSchedule();
    }

    /**
     * populate the options
     * @param frequencies
     */
    setOptions = (frequencies: string[]) => {
        let options = [];
        if (frequencies.indexOf('daily') !== -1) {
            options.push({ label: 'Daily', value: { key: 'daily', hourInputLabel: 'at this local time (24-hour clock)' } });
        }
        if (frequencies.indexOf('weekly') !== -1) {
            options.push({ label: 'Weekly', value: { key: 'weekly', hourInputLabel: `at this local time (24-hour clock)`  } });
        }
        if (frequencies.indexOf('monthly') !== -1) {
            options.push({ label: 'Monthly', value: { key: 'monthly', hourInputLabel: 'at this local time (24-hour clock)'} });
        }
        if(frequencies.indexOf('once') !== -1){
            options.push({ label: 'Run Once', value: { key: 'asap' } });
        }
        this.options = options;
    }

    /**
     * function called when the frequency is change or the time is changed
     */
    onBuildSchedule = () => {
        this.onMonthSelectedChanged.emit(this.monthSelected);
        if (this.minDate && this.hourSelected) { // decide if the selection is within the range, adjusting dates as needed.
            if (this.hourSelected > this.minDate && this.hourSelected.getDate() > this.minDate.getDate()) {
                this.hourSelected = new Date(this.hourSelected.setDate(this.hourSelected.getDate() - 1));
            }
            if (this.hourSelected < this.minDate && this.hourSelected.getHours() < this.maxDate.getHours()) {
                this.hourSelected = new Date(this.hourSelected.setDate(this.hourSelected.getDate() + 1));
            }
            this.isInvalidDate = this.hourSelected < this.minDate || this.hourSelected > this.maxDate;
        }
        if (this.selectedOption) {
            this.showMonthlyTimeFrames = this.selectedOption.key === 'asap';
            if(this.hourSelected || this.selectedOption.key === 'asap'){
                let schedule = this.buildSchedule(this.selectedOption.key, this.hourSelected);
                if (schedule) {
                    this.onCadenceChanged.emit(schedule);
                }
            }
        }
    }
    /**
     * Build the schedule time using cargo types
     */
    buildSchedule = (selectedOptionKey: string, timeSelected: Date | null) => {
        let schedule;
        if(!timeSelected){
            timeSelected = new Date();
        }
        switch (selectedOptionKey) {
            case 'daily':
                schedule = this.buildDailySchedule(timeSelected);
                break;
            case 'weekly':
                schedule = this.buildWeeklySchedule(timeSelected);
                break;
            case 'monthly':
                schedule = this.buildMonthlySchedule(timeSelected);
                break;
            case 'asap':
                schedule = 'asap';
                break;
            case 'default':
                schedule = undefined;
        }
        return schedule;
    }

    /**
     * build weekly schedule
     */
    buildWeeklySchedule = (inputDate: Date): { weekly: CargoReportWeeklyScheduleV2 } => {
        let customerTime = new Date(inputDate);
        customerTime.setHours(inputDate.getHours());
        customerTime.setMinutes(inputDate.getMinutes());
        return {
            weekly: {
                day: this.scheduledDayOfWeek || this.getDayName(1),
                hour: customerTime.getUTCHours(),
                minute: customerTime.getUTCMinutes()
            }
        };
    }

    /**
     * Get the name of the day of the week
     * */
    getDayName = (day: number): 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' => {
        const daysOfWeek: any = {
            '0': 'sunday',
            '1': 'monday',
            '2': 'tuesday',
            '3': 'wednesday',
            '4': 'thursday',
            '5': 'friday',
            '6': 'saturday'
        };

        if (day >= 0 && day < 7) {
            return daysOfWeek[day];
        } else {
            console.warn("ERROR: requesting the name for the day :", day);
        }
        return daysOfWeek['1'];
    }

    /**
     * build daily schedule
     */
    buildDailySchedule = (inputDate: Date): { daily: CargoReportDailyScheduleV2 } => {
        return {
            daily: {
                hour: inputDate.getUTCHours(),
                minute: inputDate.getUTCMinutes()
            }
        };
    }

    /**
     * build monthly schedule
     */
    buildMonthlySchedule = (inputDate: Date): { monthly: CargoReportMonthlyScheduleV2 } => {
        let customerTime: Date = new Date();
        customerTime.setDate(this.scheduledDayOfMonth || 1);
        customerTime.setHours(inputDate.getHours());
        customerTime.setMinutes(inputDate.getMinutes());

        return {
            monthly: {
                day: customerTime.getUTCDate(),
                hour: customerTime.getUTCHours(),
                minute: customerTime.getUTCMinutes()
            }
        };
    }
}
